// Prevent a data property (i.e., one defined by a value rather than by
// accessors) from being changed or configured. By default, the existing value
// of the property is used, but a different value can be provided.
// Note that native properties on DOM objects are not normal JavaScript
// properties and cannot be frozen using Object.freeze(), but this function
// *can* freeze them, because it defines a new property that overwrites the
// native one. (Obviously that doesn't mean that the underlying native state is
// frozen, though; it just can't be changed through the property anymore.)
export function freezeDataProperty<O extends Object, V extends keyof O>(
  object: O,
  property: V,
  value: O[V] = object[property],
) {
  // Freezing a property that is already frozen (configurable: false), will
  // throw an error preventing playback from initializing. This can happen, for example,
  // when a browser extension modifies the property prior to this execution (see CH78462).
  // The freeze process is a component of our "defense in depth" strategy to prevent malicious
  // acts such as mutating iframe src. The following conditional is a compromise to allow
  // execution to continue even when we are unable to fully control the original state of this
  // property, relying instead on our content security policies to lock down any bad actors.
  // While this is not an ideal situaion, our redundant protections help us work around an otherwise
  // gnarly usability problem (playback outright failing for multiple customers running various
  // extensions). If we later become uncomfortable with this compromise, I recommend changing
  // the warning below to a hard error.
  const descriptor = Object.getOwnPropertyDescriptor(object, property);
  if (descriptor && !descriptor.configurable) {
    console.warn(
      `Failed to freeze ${object} property '${String(
        property,
      )}'. Try disabling your browser extensions.`,
    );
    return;
  }

  Object.defineProperty(object, property, {
    configurable: false,
    writable: false,
    value,
  });
}

// Prevent the given data properties from being changed, freezing them at their
// existing values. See freezeDataProperty() for more details.
export function freezeDataProperties<O extends Object>(object: O, properties: (keyof O)[]) {
  for (const property of properties) {
    freezeDataProperty(object, property);
  }
}

// Prevent an accessor property (i.e., one defined by a getter and/or a setter,
// rather than a value) from being changed or configured. A new getter must be
// provided, since the original may have had side effects. See
// freezeDataProperty() for more details.
export function freezeAccessorProperty<O extends Object, V extends keyof O>(
  object: Object,
  property: keyof O,
  getter: () => O[V],
) {
  Object.defineProperty(object, property, {
    configurable: false,
    get: getter,
  });
}

// Prevent setting the attributes in 'attrs' via any standard DOM method. You
// can still change them via e.g. `Element.prototype.setAttribute.call()`, and
// they can still potentially be changed indirectly by native methods that may
// affect them, so this isn't bulletproof security. The goal is to prevent naive
// or buggy code from changing them accidentally.
export function freezeAttributes(elem: Element, attrs: string[]) {
  const throwIfFrozen = (name: string) => {
    if (attrs.includes(name.toLowerCase())) {
      throw new Error(`Trying to set frozen attribute ${name} on a controlled iframe`);
    }
  };

  freezeDataProperty(elem, 'setAttribute', (name: string, value: string) => {
    throwIfFrozen(name);
    return Element.prototype.setAttribute.call(elem, name, value);
  });

  freezeDataProperty(elem, 'setAttributeNS', (ns: string | null, name: string, value: string) => {
    throwIfFrozen(name);
    return Element.prototype.setAttributeNS.call(elem, ns, name, value);
  });

  freezeDataProperty(elem, 'setAttributeNode', (attr: Attr) => {
    throwIfFrozen(attr.localName);
    return Element.prototype.setAttributeNode.call(elem, attr);
  });

  freezeDataProperty(elem, 'setAttributeNodeNS', (attr: Attr) => {
    throwIfFrozen(attr.localName);
    return Element.prototype.setAttributeNodeNS.call(elem, attr);
  });

  freezeDataProperty(elem, 'toggleAttribute', (name: string, force?: boolean) => {
    throwIfFrozen(name);
    return Element.prototype.toggleAttribute.call(elem, name, force);
  });

  freezeDataProperty(elem, 'removeAttribute', (name: string) => {
    throwIfFrozen(name);
    return Element.prototype.removeAttribute.call(elem, name);
  });

  freezeDataProperty(elem, 'removeAttributeNS', (ns: string | null, name: string) => {
    throwIfFrozen(name);
    return Element.prototype.removeAttributeNS.call(elem, ns, name);
  });

  freezeDataProperty(elem, 'removeAttributeNode', (attr: Attr) => {
    throwIfFrozen(attr.localName);
    return Element.prototype.removeAttributeNode.call(elem, attr);
  });
}
